import { Button } from '@1trading/react/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { format } from 'date-fns/format';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingBackButton, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import SignatureInput from '@/components/ui/signature-input';
import { ClientCategorisation } from '@/constants';
import { type RouteConfig, useRouteConfig } from '@/context/route-config';
import {
  type FuturesClientCategorisationProfessionalAgreement as FuturesClientCategorisationProfessionalAgreementType,
  futuresClientCategorisationProfessionalAgreementSchema,
} from '@/features/futures/schemas/client-categorisation-professional-agreement';
import { isFuturesAllowedForCountryAndCategorisation } from '@/features/futures/utils/country-futures-eligibility';
import { useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';
import { useProfileAgreementMutation } from '@/hooks/queries/use-profile-agreement';
import { useProfileClientCategorisationTreatmentQuestionnaireMutation } from '@/hooks/queries/use-profile-client-categorisation-treatment';
import { useUserAttributes } from '@/hooks/queries/use-user-attributes';
import { AgreementKey } from '@/services/api/profile/agreement';
import { isInstitutionalUserSegment } from '@/utils/user-segment';

type FuturesClientCategorisationProfessionalAgreementProps = {
  countryOfResidency: string;
  reprocessFuturesEligibility?: boolean;
};

export interface FuturesClientCategorisationProfessionalAgreementRouteConfig extends RouteConfig {
  appropriatenessTest: string;
  clientCategorisationTreatment: string;
  jurisdictionUnavailable: string;
}

const FuturesClientCategorisationProfessionalAgreementStatement = () => {
  const { t } = useTranslation();

  return (
    <section className="space-y-6">
      <ul className="space-y-4">
        {(t('FuturesAppropriatenessClientCategorisation.professionalAgreement.statements', { returnObjects: true }) as string[]).map((text, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: index matches translation index
          <li className="rounded-md p-4 bg-background shadow-sm" key={index}>
            <p className="font-medium">{text}</p>
          </li>
        ))}
      </ul>
      <p>{t('FuturesAppropriatenessClientCategorisation.professionalAgreement.acknowledgement')}</p>
    </section>
  );
};
FuturesClientCategorisationProfessionalAgreementStatement.displayName = 'FuturesClientCategorisationProfessionalAgreementStatement';

const FuturesClientCategorisationProfessionalAgreementForm = ({
  reprocessFuturesEligibility,
}: Pick<FuturesClientCategorisationProfessionalAgreementProps, 'reprocessFuturesEligibility'>) => {
  const form = useForm<FuturesClientCategorisationProfessionalAgreementType>({
    defaultValues: {
      signature: '',
      name: '',
      date: format(new Date(), 'yyyy-MM-dd'),
    },
    resolver: standardSchemaResolver(futuresClientCategorisationProfessionalAgreementSchema),
  });
  const { isPending: isPendingClientCategorisationTreatmentQuestionnaire, mutateAsync: mutateAsyncClientCategorisationTreatmentQuestionnaire } =
    useProfileClientCategorisationTreatmentQuestionnaireMutation();
  const { isPending: isPendingPostAgreement, mutateAsync: mutateAsyncPostAgreement } = useProfileAgreementMutation();
  const routeConfig = useRouteConfig<FuturesClientCategorisationProfessionalAgreementRouteConfig>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const isPending = isPendingClientCategorisationTreatmentQuestionnaire || isPendingPostAgreement;

  const onError = () => {
    toast({
      description: t('fundamentals.defaultError.message'),
      variant: 'destructive',
    });
  };

  const onSubmit = async (data: FuturesClientCategorisationProfessionalAgreementType) => {
    try {
      await mutateAsyncPostAgreement({
        key: AgreementKey.PROFESSIONAL_CLIENT_WAIVER,
        body: data,
      });
      await mutateAsyncClientCategorisationTreatmentQuestionnaire({
        answers: { treatment: ClientCategorisation.PROFESSIONAL },
        reprocessFuturesEligibility,
      });
      navigate(routeConfig.appropriatenessTest, { replace: true });
    } catch {
      onError();
    }
  };

  return (
    <Form {...form}>
      <form autoComplete="off" name="client-categorisation-professional-agreement" onSubmit={form.handleSubmit(onSubmit)}>
        <PageCardContent className="pt-8 space-y-8">
          <FuturesClientCategorisationProfessionalAgreementStatement />
          <FormField
            control={form.control}
            name="signature"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>{t('FuturesAppropriatenessClientCategorisation.professionalAgreement.signature')}*</FormLabel>
                <FormControl>
                  <SignatureInput {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>{t('FuturesAppropriatenessClientCategorisation.professionalAgreement.name')}*</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={t('FuturesAppropriatenessClientCategorisation.professionalAgreement.placeholders.name')} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>{t('FuturesAppropriatenessClientCategorisation.professionalAgreement.date')}*</FormLabel>
                <FormControl>
                  <Input {...field} readOnly />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </PageCardContent>
        <PageCardFooter>
          <Button className="relative w-full" size="lg" type="submit" variant="primary" disabled={isPending}>
            <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
            {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
          </Button>
        </PageCardFooter>
      </form>
    </Form>
  );
};
FuturesClientCategorisationProfessionalAgreementForm.displayName = 'FuturesClientCategorisationProfessionalAgreementForm';

const FuturesClientCategorisationProfessionalAgreementContent = ({
  reprocessFuturesEligibility,
}: Pick<FuturesClientCategorisationProfessionalAgreementProps, 'reprocessFuturesEligibility'>) => {
  const routeConfig = useRouteConfig<FuturesClientCategorisationProfessionalAgreementRouteConfig>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="relative">
      <PageHeading className="relative">
        <PageHeadingTitle>{t('FuturesAppropriatenessClientCategorisation.professionalAgreement.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('FuturesAppropriatenessClientCategorisation.professionalAgreement.description')}</PageHeadingDescription>
      </PageHeading>
      <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={() => navigate(routeConfig.clientCategorisationTreatment, { replace: true })} />
      <PageCard size="lg" bordered fixedFooter>
        <FuturesClientCategorisationProfessionalAgreementForm reprocessFuturesEligibility={reprocessFuturesEligibility} />
      </PageCard>
    </div>
  );
};
FuturesClientCategorisationProfessionalAgreementContent.displayName = 'FuturesClientCategorisationProfessionalAgreementContent';

const FuturesClientCategorisationProfessionalAgreement = ({ countryOfResidency, reprocessFuturesEligibility }: FuturesClientCategorisationProfessionalAgreementProps) => {
  const { data: userAttributes } = useUserAttributes();
  const routeConfig = useRouteConfig<FuturesClientCategorisationProfessionalAgreementRouteConfig>();
  const { isLoading: isLoadingCountries, data: countries } = useMainstreamCountries();
  const { segment: userSegment } = userAttributes ?? {};

  if (isLoadingCountries) return <LoadingCard />;

  if (!isInstitutionalUserSegment(userSegment) && !isFuturesAllowedForCountryAndCategorisation(countries?.[countryOfResidency], ClientCategorisation.PROFESSIONAL)) {
    return <Navigate to={routeConfig.jurisdictionUnavailable} replace />;
  }

  return <FuturesClientCategorisationProfessionalAgreementContent reprocessFuturesEligibility={reprocessFuturesEligibility} />;
};
FuturesClientCategorisationProfessionalAgreement.displayName = 'FuturesClientCategorisationProfessionalAgreement';

export default FuturesClientCategorisationProfessionalAgreement;
